import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar, Box } from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getScopeMap } from '../../auth-config';
import { AccountPicker } from './AccountPicker';
import useAppSelector from 'hooks/useAppSelector';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import useAppDispatch from 'hooks/useAppDispatch';
import {
    selectViewMode,
    setCurrentView,
    setOrigin,
    setSelectedBatchStatus,
    setSelectedStatus,
    setTargetInputMode,
    setViewMode,
} from 'store/reducers/navigationSlice';
import { resetOrderState } from 'store/reducers/orderSlice';
import { resetReturnState } from 'store/reducers/returnSlice';
import { resetBatchReturnState } from 'store/reducers/batchReturnSlice';
import { BATCH_RETURN_COLOR } from 'components/batch-returns/BatchReturn';
import { isBatchRoute } from 'utils/navigation';

export const SignInSignOutButton = () => {
    const { inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    if (isAuthenticated) {
        return <SignOutButton />;
    } else if (inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect) {
        // inProgress check prevents sign-in button from being displayed briefly after returning from a redirect sign-in. Processing the server response takes a render cycle or two
        return <SignInButton />;
    } else {
        return null;
    }
};

export const SignInButton = () => {
    const { instance } = useMsal();
    const { t } = useTranslation();

    const handleLogin = () => {
        instance.loginRedirect(getScopeMap().login);
    };

    return (
        <Button onClick={() => handleLogin()} color="inherit">
            {t('signIn')}
        </Button>
    );
};

export const SignOutButton = () => {
    const { instance } = useMsal();
    const { t } = useTranslation();
    const location = useLocation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const viewMode = useAppSelector(selectViewMode);
    const siteSettings = useAppSelector(companiesSelector.selectDisplaySettings);

    const [accountSelectorOpen, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const open = Boolean(anchorEl);

    const returnView = viewMode === 'returns';
    const batchRoute = isBatchRoute(location.pathname);

    const handleLogout = () => {
        setAnchorEl(null);
        // //We don't want to logut from the ad. Only delete from localstorage
        localStorage.clear();
        // Remove the active account from MSAL
        instance.setActiveAccount(null);

        window.location.href = '/';
    };

    const handleAccountSelection = () => {
        setAnchorEl(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setAnchorEl(null);
    };

    const handleNavigation = (path: string) => {
        navigate(path);
        dispatch(setCurrentView(path));
        setAnchorEl(null);
    };

    const handleToggleViewMode = () => {
        const nextViewMode = returnView ? 'batch-returns' : 'returns';
        const nextPath = returnView ? '/batch-returns' : '/';

        // Reset navigation state when switching between returns and batch returns
        dispatch(setSelectedStatus(null));
        dispatch(setSelectedBatchStatus(null));
        dispatch(setOrigin(null));
        dispatch(setTargetInputMode(null));
        dispatch(setCurrentView('default'));

        dispatch(resetOrderState());
        dispatch(resetReturnState());
        dispatch(resetBatchReturnState());

        dispatch(setViewMode(nextViewMode));
        navigate(nextPath);
    };

    return (
        <div>
            <Box sx={{ cursor: 'pointer', width: 'fit-content' }} onClick={(event) => setAnchorEl(event.currentTarget)}>
                <Avatar
                    style={{
                        width: '27px',
                        height: '27px',
                        backgroundColor: batchRoute ? BATCH_RETURN_COLOR : siteSettings?.accentThemeColor ?? undefined,
                    }}
                />
            </Box>
            <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={open}
                onClick={handleClose}
                onClose={handleClose}
                MenuListProps={{ autoFocus: false, autoFocusItem: false }}
            >
                <MenuItem onClick={() => handleToggleViewMode()}>
                    {returnView ? t('batchReturns', 'Batch returns') : t('returns', 'Returns')}
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/admin')}>Admin</MenuItem>
                <MenuItem onClick={() => handleAccountSelection()} key="switchAccount">
                    {t('switchAccount')}
                </MenuItem>
                <MenuItem onClick={() => handleLogout()} key="logoutRedirect">
                    {t('logout')}
                </MenuItem>
            </Menu>
            <AccountPicker open={accountSelectorOpen} onClose={handleClose} />
        </div>
    );
};
